// ── Core Type Definitions for DIG DEEP ──

export type ToolType = 'pickaxe' | 'brush' | 'trowel' | 'gpr' | 'charges';
export type ArtifactType = 'pottery' | 'weapon' | 'jewelry' | 'tool' | 'bone' | 'document' | 'structure' | 'art' | 'coin' | 'religious';
export type Era = 'neolithic' | 'bronze_age' | 'iron_age' | 'classical' | 'medieval' | 'renaissance' | 'ancient' | 'prehistoric' | 'proto_human';
export type Culture = 'native_american' | 'roman' | 'mayan' | 'chinese' | 'persian' | 'viking' | 'egyptian' | 'japanese' | 'greek' | 'mediterranean' | 'saharan' | 'proto_human' | 'multi_cultural' | 'unknown';
export type Condition = 'pristine' | 'good' | 'fair' | 'damaged' | 'fragments';
export type Rarity = 'common' | 'uncommon' | 'rare' | 'legendary' | 'mythical';
export type AnalysisState = 'dirty' | 'cleaned' | 'examined' | 'dated' | 'cross_referenced' | 'documented';
export type WeatherType = 'clear' | 'rain' | 'heat_wave' | 'storm' | 'perfect';
export type ScreenType = 'menu' | 'dig' | 'lab' | 'museum' | 'map' | 'journal' | 'skills';
export type ReputationTier = 'student' | 'field_researcher' | 'respected_scholar' | 'distinguished_professor' | 'living_legend';
export type MaterialType = 'topsoil' | 'clay' | 'sediment' | 'rock' | 'bedrock' | 'sand' | 'permafrost' | 'water' | 'volcanic';
export type SkillBranch = 'excavation' | 'analysis' | 'business' | 'intuition';

export interface ToolDef {
  type: ToolType;
  name: string;
  icon: string;
  speed: number;         // time units per dig action
  damageCh: number;      // chance to damage artifact (0-1)
  cost: number;          // cost per use (0 for most)
  areaEffect?: number;   // tiles affected (charges = 3x3)
}

export interface LayerDef {
  material: MaterialType;
  hardness: number;      // 1-10, affects dig time
  depth: number;         // layer index
}

export interface TileDef {
  layers: LayerDef[];
  currentLayer: number;
  artifactId: string | null;
  artifactLayer: number;
  revealed: boolean;
  marked: boolean;       // for crew auto-dig
  scanned: boolean;      // GPR scanned
}

export interface ArtifactDef {
  id: string;
  name: string;
  type: ArtifactType;
  era: Era;
  culture: Culture;
  baseValue: number;
  rarity: Rarity;
  fragile: boolean;
  storyFragmentId: string;
  museumAppeal: number;  // 1-10
  description: string;
  icon: string;
}

export interface ArtifactInstance {
  defId: string;
  condition: Condition;
  analysisState: AnalysisState;
  analysisProgress: number;  // 0-100 for current step
  discovered: boolean;
  siteId: string;
  gridX: number;
  gridY: number;
  sold: boolean;
  displayed: boolean;
  displaySlot: number;
  uid: string;
}

export interface StoryFragment {
  id: string;
  siteId: string;
  text: string;
  order: number;        // position in the narrative
  artifactId: string;   // which artifact reveals this
  connection?: string;   // links to another fragment
}

export interface TheoryDef {
  id: string;
  siteId: string;
  question: string;
  options: string[];
  correctIndex: number;
  requiredFragments: number;  // % of fragments needed
  repReward: number;
  loreText: string;
}

export interface SiteDef {
  id: string;
  name: string;
  location: string;
  tier: number;
  gridSize: number;
  maxLayers: number;
  seasonDays: number;
  permitCost: number;
  travelCost: number;
  repRequired: number;
  description: string;
  layerTypes: MaterialType[];
  specialMechanic?: 'water_table' | 'permafrost' | 'underwater';
  ambientSound: string;
  mapX: number;       // percentage position on world map
  mapY: number;
}

export interface MuseumBuyerDef {
  id: string;
  name: string;
  preferredTypes: ArtifactType[];
  preferredEras: Era[];
  baseMultiplier: number;
  relationship: number;  // 0-100, improves with sales
}

export interface SkillDef {
  id: string;
  name: string;
  branch: SkillBranch;
  tier: number;        // 1-4
  cost: number;        // skill points
  description: string;
  effect: string;      // parsed by game logic
  prerequisite?: string;
}

export interface CrewMember {
  id: string;
  name: string;
  skill: number;       // 1-5 efficiency
  wage: number;        // per day
  active: boolean;
}

export interface DisplaySlot {
  index: number;
  artifactUid: string | null;
  x: number;
  y: number;
}

export interface SynergyBonus {
  type: 'cultural_collection' | 'narrative_display' | 'showpiece';
  name: string;
  multiplier: number;
  artifactUids: string[];
}

export interface SaveData {
  version: number;
  playerName: string;
  money: number;
  reputation: number;
  currentSiteId: string | null;
  currentDay: number;
  artifacts: ArtifactInstance[];
  discoveredFragments: string[];
  submittedTheories: { theoryId: string; correct: boolean }[];
  skills: string[];     // unlocked skill IDs
  skillPoints: number;
  museumSlots: DisplaySlot[];
  museumUpgrades: string[];
  buyerRelationships: { buyerId: string; relationship: number }[];
  crew: CrewMember[];
  sitesCompleted: string[];
  siteCompletionStars?: Record<string, number>;
  totalArtifactsFound: number;
  totalTheoriesCorrect: number;
  tutorialComplete: boolean;
  currentWeather: WeatherType;
  weatherDaysLeft: number;
  siteGridState?: TileDef[][];
  siteArtifacts?: ArtifactInstance[];  // all dig site artifacts (including undiscovered)
  timestamp: number;
}

export interface GameConfig {
  conditionMultipliers: Record<Condition, number>;
  rarityMultipliers: Record<Rarity, number>;
  repTiers: { tier: ReputationTier; min: number; label: string }[];
  weatherEffects: Record<WeatherType, { timeMultiplier: number; description: string }>;
}

export const GAME_CONFIG: GameConfig = {
  conditionMultipliers: {
    pristine: 2.0,
    good: 1.5,
    fair: 1.0,
    damaged: 0.5,
    fragments: 0.2
  },
  rarityMultipliers: {
    common: 1.0,
    uncommon: 2.0,
    rare: 5.0,
    legendary: 15.0,
    mythical: 50.0
  },
  repTiers: [
    { tier: 'student', min: 0, label: 'Student' },
    { tier: 'field_researcher', min: 20, label: 'Field Researcher' },
    { tier: 'respected_scholar', min: 40, label: 'Respected Scholar' },
    { tier: 'distinguished_professor', min: 65, label: 'Distinguished Professor' },
    { tier: 'living_legend', min: 85, label: 'Living Legend' }
  ],
  weatherEffects: {
    clear: { timeMultiplier: 1.0, description: 'Clear Skies' },
    rain: { timeMultiplier: 1.5, description: 'Rainy — work is slower' },
    heat_wave: { timeMultiplier: 1.2, description: 'Heat Wave — crew morale drops' },
    storm: { timeMultiplier: 999, description: 'Storm — no work today' },
    perfect: { timeMultiplier: 0.75, description: 'Perfect Conditions!' }
  }
};

export const TOOLS: Record<ToolType, ToolDef> = {
  pickaxe: { type: 'pickaxe', name: 'Pickaxe', icon: '⛏️', speed: 1, damageCh: 0.3, cost: 0 },
  brush: { type: 'brush', name: 'Brush', icon: '🖌️', speed: 3, damageCh: 0, cost: 0 },
  trowel: { type: 'trowel', name: 'Trowel', icon: '🔧', speed: 2, damageCh: 0.05, cost: 0 },
  gpr: { type: 'gpr', name: 'GPR Scanner', icon: '📡', speed: 0, damageCh: 0, cost: 150 },
  charges: { type: 'charges', name: 'Excavation Charges', icon: '💥', speed: 0.5, damageCh: 0.5, cost: 300, areaEffect: 3 }
};
